import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Updater } from 'use-immer'
import { ConfigProps, StationObjProps } from '../../../page/Map'
import { CircularFilterConfig } from '../../../../type/SubjectProps'
import { StationProps } from '../../../../type/StationProps'
import {
    fetchCircularSubject,
    fetchCircularSubjects,
} from '../../../../utils/fetchCircular'

interface Props {
    config: ConfigProps
    filterConfig: CircularFilterConfig
    subjectId?: string
    updateConfig: Updater<ConfigProps>
    updateStationsObj: Updater<StationObjProps>
    setShowSpinner: Dispatch<SetStateAction<boolean>>
}

export const useFetchCircularData = ({
    config,
    filterConfig,
    subjectId,
    updateConfig,
    updateStationsObj,
    setShowSpinner,
}: Props) => {
    const { position, type } = config
    const [resolved, setResolved] = useState<{
        id: string
        station?: StationProps
    }>()
    const [message, setMessage] = useState('')
    const pendingSelection = useRef<StationProps>()

    useEffect(() => {
        if (type !== 'circular') return
        setMessage('')
        pendingSelection.current = undefined
        if (!subjectId) {
            setResolved(undefined)
            return
        }
        const controller = new AbortController()
        setShowSpinner(true)
        updateStationsObj((draft) => {
            draft.stations = []
            draft.activeStation = undefined
        })
        fetchCircularSubject(subjectId, controller.signal)
            .then((station) => {
                if (controller.signal.aborted) return
                if (station) {
                    station.distance = 0
                    pendingSelection.current = station
                    const [lng, lat] = station.geometry.coordinates
                    updateConfig((draft) => {
                        draft.position = { lat, lng }
                        draft.filterOpen = false
                    })
                } else {
                    setMessage(
                        'Subjekt není dostupný nebo nemá platné souřadnice. Zobrazuje se běžná mapa.'
                    )
                }
                setResolved({ id: subjectId, station })
            })
            .catch(() => {
                if (controller.signal.aborted) return
                setMessage(
                    'Subjekt se nepodařilo načíst. Zobrazuje se běžná mapa.'
                )
                setResolved({ id: subjectId })
            })
        return () => controller.abort()
    }, [subjectId, type, updateConfig, updateStationsObj, setShowSpinner])

    useEffect(() => {
        if (
            type !== 'circular' ||
            !position ||
            (subjectId && resolved?.id !== subjectId)
        )
            return
        const controller = new AbortController()
        const selected = pendingSelection.current
        setShowSpinner(true)
        updateConfig((draft) => {
            draft.state = 'idle'
        })
        updateStationsObj((draft) => {
            draft.stations = selected ? [selected] : []
            draft.activeStation = selected
        })
        fetchCircularSubjects(position, filterConfig, controller.signal)
            .then((stations) => {
                if (controller.signal.aborted) return
                if (
                    selected &&
                    !stations.some(
                        (station) =>
                            station.properties.id === selected.properties.id
                    )
                )
                    stations.unshift(selected)
                updateStationsObj((draft) => {
                    draft.stations = stations
                    draft.activeStation = selected
                        ? stations.find(
                              (station) =>
                                  station.properties.id ===
                                  selected.properties.id
                          )
                        : undefined
                })
                pendingSelection.current = undefined
                updateConfig((draft) => {
                    draft.state = 'init'
                })
            })
            .catch(() => {
                if (controller.signal.aborted) return
                pendingSelection.current = undefined
                updateConfig((draft) => {
                    draft.state = 'error'
                })
            })
            .finally(() => {
                if (!controller.signal.aborted) setShowSpinner(false)
            })
        return () => controller.abort()
    }, [
        position,
        type,
        filterConfig,
        subjectId,
        resolved,
        updateConfig,
        updateStationsObj,
        setShowSpinner,
    ])
    return message
}
